import { ReactNode, useState } from "react";
import { useTimer } from "../../hooks/useTimer";
import { useBotbPassDrawerContext } from "./botb-pass-drawer-context";
import Markdown from "react-markdown";

const DrawerWrapper = ({ children }: { children: ReactNode }) => {
  const [isPostalEntryTextOpen, setIsPostalEntryTextOpen] = useState(false);
  const { preSubscriptionData } = useBotbPassDrawerContext();
  const { hours, minutes, seconds, milliseconds } = useTimer(
    "upsell-subscription-modal",
    (preSubscriptionData?.offer?.expireTime || 0) * 1000,
    undefined,
    "millisecond"
  );
  const milisecondsT = Math.floor(milliseconds / 10)
    .toString()
    .padStart(2, "0");
  const hasCounter = preSubscriptionData?.offer?.expireTime > 0;
  const hasOffer = !!preSubscriptionData.offer;

  return (
    <>
      {/*START*/}
      {/*TITLE ZONE*/}
      <div className={"mb-4 font-jost px-4 lg:mb-8"}>
        <div
          className={`${
            hasCounter
              ? "max-lg:text-center lg:flex lg:flex-row-reverse lg:items-center lg:gap-2 lg:justify-end lg:mb-3"
              : "text-center"
          }`}>
          {hasCounter && (
            <div
              className={"inline-flex mb-0.5 px-2 py-1 lg:p-2 rounded-lg bg-gradient-to-r from-[#431CA0] to-[#7230E7]"}>
              <span className={"text-white font-jost text-sm lg:text-lg font-normal !leading-4 whitespace-nowrap"}>
                Ends in{" "}
                <span className={"min-w-[17px] lg:min-w-[22px] inline-flex text-right justify-end"}> {hours}</span>h :{" "}
                <span className={"min-w-[17px] lg:min-w-[22px] inline-flex text-right justify-end"}>{minutes}</span>m :{" "}
                <span className={"min-w-[17px] lg:min-w-[22px] inline-flex text-right justify-end"}>{seconds}</span>
                <span className={"inline-flex min-w-3 lg:min-w-3.5 text-[9px] lg:text-xs !leading-none"}>
                  {milisecondsT}
                </span>
              </span>
            </div>
          )}
          <div
            className={`text-xl font-semibold font-jost leading-6.5 text-dark-1 lg:text-[28px] lg:leading-8  ${
              hasCounter ? "" : "lg:mb-3"
            }`}>
            <Markdown
              components={{
                p: props => props.children,
              }}>
              {hasOffer ? preSubscriptionData.offer.headline : preSubscriptionData.header.title}
            </Markdown>
          </div>
        </div>
        {hasOffer ? (
          <div
            className={`text-secondary text-sm font-normal max-lg:text-center ${hasCounter ? "" : "lg:text-center"} leading-5 max-lg:px-4 lg:text-xl lg:leading-6.5`}>
            <Markdown
              components={{
                p: props => props.children,
              }}>
              {preSubscriptionData.offer.bonus}
            </Markdown>{" "}
            <Markdown
              components={{
                p: props => props.children,
              }}>
              {preSubscriptionData.offer.description}
            </Markdown>
          </div>
        ) : (
          <div className={"text-secondary text-sm font-normal text-center leading-5 px-4 lg:text-xl lg:leading-6.5"}>
            <Markdown components={{ p: props => props.children }}>{preSubscriptionData.header.subtitlep1}</Markdown>{" "}
            <Markdown components={{ p: props => <span className={"text-primary font-medium"}>{props.children}</span> }}>
              {preSubscriptionData.header.subtitlep2}
            </Markdown>{" "}
            <Markdown components={{ p: props => props.children }}>{preSubscriptionData.header.subtitlep3}</Markdown>
          </div>
        )}
      </div>
      {/*BOXES START*/}
      <div className={"max-lg:mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-start"}>{children}</div>
      {/*BOXES END*/}

      <div className={"flex flex-col lg:flex-col-reverse lg:mt-[38px]"}>
        <div className={"text-center text-[11px] font-jost text-gray-5 leading-3.5 lg:text-xs lg:leading-4"}>
          {preSubscriptionData.header.legalText}
        </div>

        {/*Free postal entry*/}
        <div
          className={`mt-4 lg:mt-0 lg:mb-10 border border-black/8 bg-white max-lg:shadow-[0px_4px_32px_0px_rgba(0,0,0,0.10)] rounded-2xl lg:max-w-[680px] lg:mx-auto lg:relative`}>
          <div className={"p-4"}>
            <div
              className={`max-lg:max-h-[176px] overflow-y-auto lg:transition-all lg:duration-500 lg:ease-in-out ${
                isPostalEntryTextOpen ? "lg:max-h-[400px] lg:overflow-y-auto" : "lg:overflow-y-hidden lg:max-h-[86px]"
              }`}>
              {/*<div className={"font-jost text-lg font-medium mb-4 lg:mb-2 leading-6"}>Free postal entry</div>*/}
              {/*<div className={"text-xs leading-5 mb-6"}>*/}
              {/*    <p className={"font-medium text-dark-1"}>Postal Entry for Competitions</p>*/}
              {/*    <p className={"font-normal text-secondary"}>*/}
              {/*        BOTB offers an alternative method of participating in competitions through Postal Entry, which is detailed below. This method does not require payment beyond the cost of postage.*/}
              {/*    </p>*/}
              {/*</div>*/}
              {/*<div className={"text-xs leading-5 lg:mb-8"}>*/}
              {/*    <p className={"font-medium text-dark-1"}>Conditions for Postal Entry</p>*/}
              {/*    <p className={"font-normal text-secondary"}>*/}
              {/*        By using the Postal Entry route, you confirm that you have the legal capacity to participate. You also agree to adhere to our Terms and Conditions and any additional requirements outlined in related promotional materials.*/}
              {/*    </p>*/}
              {/*</div>*/}

              <div className={"font-jost text-lg font-medium mb-4 lg:mb-2 leading-6 text-black"}>Free postal entry</div>
              <div
                className="
                              [&>*]:normal-case [&>p]:text-xs [&>p]:leading-5 [&>p]:font-normal [&>p]:text-secondary
                              [&>ul>li]:text-xs [&>ul>li]:leading-5 [&>ul>li]:list-disc [&>ul>li]:ml-4 [&>ul>li+li]:mt-1
                              [&>h2]:text-xs [&>h2]:leading-5 [&>h2]:text-left [&>h2]:font-medium [&>h2]:text-dark-1 [&>h2]:mt-4 lg:mb-8
                            ">
                <h2>Postal Entry for Competitions</h2>
                <p>
                  BOTB offers an alternative method of participating in competitions through Postal Entry, which is
                  detailed below. This method does not require payment beyond the cost of postage.
                </p>
                <h2>Conditions for Postal Entry</h2>
                <p>
                  By using the Postal Entry route, you confirm that you have the legal capacity to participate. You also
                  agree to adhere to our Terms and Conditions and any additional requirements outlined in related
                  promotional materials.
                </p>
                <h2>How to Enter</h2>
                <p>
                  To enter a competition by Postal Entry, send a handwritten postcard or letter with the following
                  information:
                </p>
                <ul>
                  <li>- Full name</li>
                  <li>- Address, including city and postcode</li>
                  <li>- Telephone number</li>
                  <li>- Registered email address for your BOTB Account</li>
                  <li>- Name of the Competition you wish to enter</li>
                </ul>
                <p>
                  Ensure that your handwriting is clear. Mechanically reproduced entries are not permitted. Entries must
                  be sent by post to the following address: 2 Plato Place, 72-74 St Dionis Road, London, SW6 4TU, United
                  Kingdom, with appropriate postage paid.
                </p>
                <h2>Entry Requirements</h2>
                <p>
                  Entries must be sent during the Promotion Period of the competition. Entries received within 2 working
                  days of the competition closing will be included in the draw. Entries postmarked outside the Promotion
                  Period will not be considered valid.
                </p>
                <p>
                  Only one entry is allowed per postcard or envelope. Bulk entries sent in the same envelope will not be
                  accepted. However, you may send as many separate Postal Entries as you wish, up to a maximum of 600
                  entries per person per competition.
                </p>
                <h2>Confirmation of Entry</h2>
                <p>
                  You will not be officially entered into the Competition until we confirm your Entry via email or
                  through your registered BOTB Account.
                </p>
                <h2>Validity of Entries</h2>
                <p>
                  Entries that do not comply with these guidelines will be considered void. A valid Postal Entry will be
                  included in the prize draw alongside paid-for Online Tickets, with each Postal Entry having an equal
                  chance of winning.
                </p>
                <p>
                  It is essential to have an active account on our website when Postal Entries are processed, and all
                  details provided must match those on your user account.
                </p>
              </div>
            </div>
          </div>
          <div
            onClick={() => {
              setIsPostalEntryTextOpen(prevState => !prevState);
            }}
            className={
              "text-center max-lg:hidden z-40 absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-40% from-white pt-4 cursor-pointer rounded-b-2xl"
            }>
            <span className={"text-primary text-xs font-medium font-jost leading-4"}>
              {isPostalEntryTextOpen ? "View Less" : "View More"}
            </span>
          </div>
        </div>
      </div>

      {/*END*/}
    </>
  );
};

export default DrawerWrapper;
